/** @jsxRuntime automatic */
// Side-effect import: the ambient `react-dom` types (flushSync), see that file.
import "./react-dom-types";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { handoffFocus, type FocusTarget } from "./focus-handoff";
import { useLiveInsert } from "./live-change";
import { MOTION, motionMs, whenAnimationsDone } from "./motion";
import type { HeightFollower } from "./collapse-follow";
import { cn } from "./utils";

export type CollapseAppear = "live" | "always" | "never";
export type CollapseDefer = "none" | "offscreen";

export interface CollapseProps {
  open: boolean;
  /**
   * When it mounts already open: `live` (default) grows only if it mounted
   * inside a `LiveScope` that was already live (never on a first paint, a
   * remount, a back or a restore); `always` grows on every mount; `never`
   * mounts at rest.
   */
  appear?: CollapseAppear;
  /**
   * `offscreen`, for a removal with no input (a snapshot, a timer): on screen
   * it never closes by itself. It waits until the block is out of its
   * scroller's view, and leaves with no animation and the scroll policy
   * (above the view: the engine's anchoring, or `compensateScroll` after
   * `whenScrollIdle` where there is none); or until the next tap or key
   * outside it, and closes as usual, the person's moment.
   */
  defer?: CollapseDefer;
  /** Opens and closes with no motion (many at once: Help's search). */
  instant?: boolean;
  /**
   * While open, the height also follows its content when it changes size (a
   * panel that changes in place: the login's steps, a Swap of panel), from
   * where it is in 200 `--ease-move`; a change of width lands at once. Only
   * where the change follows an input: a late block grows at once, with the
   * scroll policy (P11). Passed, even as `false`, the content keeps the same
   * wrapper while it toggles: a `follow` that turns on after a load never
   * remounts what is inside (the thread's composer kept its focus and text).
   */
  follow?: boolean;
  /**
   * Where the focus goes when the content holds it as it closes (P15): then
   * the element focused when it opened, then the nearest control (after it,
   * then before it). Never <body>.
   */
  focusTarget?: FocusTarget;
  /** Its close ended and the children unmounted. */
  onExited?: () => void;
  id?: string;
  /** Classes for the grid that animates (the outer element); a utility wins over its rules (`@layer components`). */
  className?: string;
  /** Classes for the clip that holds the content; a utility wins over its rules too. */
  contentClassName?: string;
  "data-testid"?: string;
  children: ReactNode;
}

/** Nothing moves without Web Animations or frames (jsdom): every change lands at once. */
function canMove(): boolean {
  return typeof requestAnimationFrame === "function" && typeof Element !== "undefined" && typeof Element.prototype.getAnimations === "function";
}

/** Rows set with no transition: `fr` and px do not interpolate (the rows would flip halfway). */
function snapRows(root: HTMLElement, rows: string): void {
  root.style.transitionProperty = "none";
  root.style.gridTemplateRows = rows;
  void root.offsetHeight;
  root.style.transitionProperty = "";
}

/** Something finite runs on the element or its content (`whenAnimationsDone`'s own test, read synchronously). */
function animating(el: Element): boolean {
  if (typeof el.getAnimations === "function") {
    return el.getAnimations({ subtree: true }).some((a) => a.effect?.getTiming().iterations !== Infinity);
  }
  const name = typeof getComputedStyle === "function" ? getComputedStyle(el).animationName : "";
  return !!name && name !== "none";
}

// overlay-utils has the same list, but importing it would bring the overlay
// behavior into the login's boot, where no overlay is.
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The controls nearest to `el`, the focus's last resort (P15): in its closest
 * ancestor that has any outside it, the ones after it (the next row's action,
 * the card's own button) and then the ones before it, nearest first.
 */
function nearbyControls(el: Element): Element[] {
  for (let box = el.parentElement; box; box = box.parentElement) {
    const controls = [...box.querySelectorAll(FOCUSABLE)].filter((node) => !el.contains(node) && !node.closest("[inert]"));
    if (controls.length === 0) continue;
    const after = controls.filter((node) => el.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING);
    return [...after, ...controls.filter((node) => !after.includes(node)).reverse()];
  }
  return [];
}

// What few Collapses use loads on demand: the boot carries only the Collapse
// (Callout and the toast are in it), not the deferred removal with the
// scroll policy nor the height that follows its content. A chunk that fails
// (offline after a deploy) is not kept: the next one asks again.
let deferModule: Promise<typeof import("./collapse-defer")> | undefined;
const loadDefer = () =>
  (deferModule ??= import("./collapse-defer").catch((error: unknown) => {
    deferModule = undefined;
    throw error;
  }));
let followModule: Promise<typeof import("./collapse-follow")> | undefined;
const loadFollow = () =>
  (followModule ??= import("./collapse-follow").catch((error: unknown) => {
    followModule = undefined;
    throw error;
  }));

/**
 * The height of a block that appears, grows or leaves in the flow (spec 07 ›
 * Movimiento, §4.7): grid rows `0fr ↔ 1fr` (never `height`, P12, and never on
 * a spring), which travel in px while they move (0 ↔ the content's height:
 * the clip is always the box, never a band under it). It opens in 200 `--ease-move` while its content arrives (.5 → 1
 * in 120, no travel), and closes with the content at 0 in 80 first and then
 * the rows in 130 `--ease-exit`; with reduced motion the rows change at once
 * and the content fades in 120. The opening is driven by an effect, Disclosure's
 * mechanism (two frames, then the rows; settled by a timer): an opening set
 * while rendering lost its update on Actividad and the body unmounted ~10 ms
 * after the click. Once settled the clip comes off, so a sticky inside keeps
 * sticking. While it closes it keeps its last children, the focus leaves first
 * (`handoffFocus`) and then it turns `inert`; the end is `whenAnimationsDone`,
 * at once when nothing animates (jsdom). A Collapse is not free of CLS: a
 * height that changes with no input in the 500 ms before it counts, so a
 * removal with no input is `defer="offscreen"`. Its spacing in a `gap` or
 * `space-y` parent stays until it unmounts: put that spacing inside it.
 */
export function Collapse({
  open,
  appear = "live",
  defer = "none",
  instant = false,
  follow: followProp,
  focusTarget,
  onExited,
  id,
  className,
  contentClassName,
  "data-testid": testId,
  children,
}: CollapseProps) {
  const follow = followProp ?? false;
  // A caller that can follow keeps the measure in the tree whether it follows
  // now or not: toggling the wrapper in and out remounted the children (state,
  // focus and a half-typed reply lost when `follow` turned on after a load).
  const measured = followProp !== undefined;
  const rootRef = useRef<HTMLDivElement>(null);
  const clipRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const latest = useRef({ focusTarget, onExited });
  const live = useLiveInsert();
  // Grows from 0 on its mount: decided once, like the scope it read.
  const [grows] = useState(() => open && !instant && (appear === "always" || (appear === "live" && live)));
  // `mounted` keeps the content while it leaves; `expanded` puts the rows at
  // 1fr two frames after it mounts; `settled` takes the clip off once open.
  const [mounted, setMounted] = useState(open);
  const [expanded, setExpanded] = useState(open && !grows);
  const [settled, setSettled] = useState(open && !grows);
  // A deferred removal holds the block open until it is out of view or the next input lets it go.
  const [letGo, setLetGo] = useState(false);
  const [held, setHeld] = useState(children);

  // Adjust state while rendering (React's documented pattern for derived state).
  // The content renders on `open` itself; `mounted` only keeps it while it
  // leaves, so a close before the opening's frames still plays its exit.
  if (open && !mounted) setMounted(true);
  if (open && held !== children) setHeld(children);
  if (open && letGo) setLetGo(false);
  const deferred = defer === "offscreen" && !instant && typeof IntersectionObserver === "function";
  const holding = !open && mounted && deferred && !letGo;
  const leaving = !open && mounted && !holding;
  // Clipped again from the first frame of a close, so a reopening clips until it lands.
  if (leaving && settled) setSettled(false);
  // Nothing to animate (`instant`, jsdom): an opening lands in the same render.
  if (open && (instant || !canMove()) && !(expanded && settled)) {
    setExpanded(true);
    setSettled(true);
  }

  useLayoutEffect(() => {
    latest.current = { focusTarget, onExited };
  });

  // What had the focus when it opened: where the focus goes back. Mounted
  // open at rest, nobody opened it (a row, a body shown from the start).
  const atRest = useRef(open && !grows);
  useLayoutEffect(() => {
    const rest = atRest.current;
    atRest.current = false;
    if (!open || rest) return;
    const active = document.activeElement;
    if (active && active !== document.body && !rootRef.current?.contains(active)) opener.current = active;
  }, [open]);

  // Opening: two frames with the rows at 0, then the content's height;
  // settled by a timer from where the rows start (Disclosure's mechanism).
  // The rows are committed inside the second frame's callback (`flushSync`:
  // the Collapse alone, its children are the same elements), so the
  // transition starts with the frame that paints it: no render and no task
  // of the page between its start and its first frame, which showed it
  // already 40 % open after a 46 ms gap (a pop, not an unfold).
  useEffect(() => {
    if (!open || (expanded && settled) || instant || !canMove()) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      setSettled(false);
      inner = requestAnimationFrame(() => flushSync(() => setExpanded(true)));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
    // Only a change of `open` starts an opening; the state it reads is the one it found.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instant]);

  // While the rows move they travel in px, from 0 to the content's height:
  // between 0fr and 1fr a track takes that fraction of a box that is itself
  // that fraction of the content, so the clip trailed the box and left a blank
  // band under it (a quarter of the content halfway). Settled, back to 1fr.
  // Landing at once, no rows of a motion stay on it: an opening that turns
  // `instant` after its first commit (LateBlock below the fold, above it or in
  // the page's entrance) had its rows pinned at 0 px, and settled, with the
  // clip off, its content spilled over what follows (the salary module drawn
  // over the description).
  useLayoutEffect(() => {
    const root = rootRef.current;
    const clip = clipRef.current;
    if (!open || !root || !clip) return;
    if (instant || !canMove()) {
      if (root.style.gridTemplateRows !== "") snapRows(root, "");
      return;
    }
    if (!expanded || settled) {
      const rows = expanded ? "" : "0px";
      if (root.style.gridTemplateRows !== rows) snapRows(root, rows);
      return;
    }
    root.style.gridTemplateRows = `${clip.scrollHeight}px`;
    // Settled when the rows land (the transition starts with the frame after this commit).
    let done = false;
    const settle = () => {
      if (!done) setSettled(true);
      done = true;
    };
    const rows = root.getAnimations().find((a) => (a as CSSTransition).transitionProperty === "grid-template-rows");
    rows?.finished.then(settle, () => {});
    const timer = window.setTimeout(settle, MOTION.ms.base + (rows ? 100 : 0));
    return () => {
      done = true;
      window.clearTimeout(timer);
    };
  }, [open, expanded, settled, instant]);

  // Closing: the focus first, then inert; the end when the content's fade and the rows are done.
  useLayoutEffect(() => {
    if (!leaving) return;
    const root = rootRef.current;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setMounted(false);
      setExpanded(false);
      setSettled(false);
      latest.current.onExited?.();
    };
    if (!root) {
      finish();
      return;
    }
    handoffFocus(root, [latest.current.focusTarget, opener.current, ...(root.contains(document.activeElement) ? nearbyControls(root) : [])]);
    // In px too: pinned at the height it shows (read before the pin: with no
    // transition the closed rows would apply at once), then on to 0.
    if (!instant && canMove()) {
      snapRows(root, `${root.getBoundingClientRect().height}px`);
      root.style.gridTemplateRows = "0px";
    }
    root.setAttribute("inert", "");
    const restore = () => root.removeAttribute("inert");
    if (instant || !animating(root)) {
      finish();
      return restore;
    }
    const abort = new AbortController();
    void whenAnimationsDone(root, {
      subtree: true,
      signal: abort.signal,
      fallbackMs: motionMs("fast-exit") + motionMs("base-exit") + 100,
    }).then(finish);
    return () => {
      abort.abort();
      restore();
    };
  }, [leaving, instant]);

  // A deferred removal (`collapse-defer.ts`, loaded as it mounts so a fling
  // under way is already known): out of view it drops at once with the scroll
  // policy; on screen, a tap or a key outside it closes it as usual.
  useEffect(() => {
    if (deferred) void loadDefer().then((module) => module.watchScrolling(), () => {});
  }, [deferred]);
  useEffect(() => {
    const root = rootRef.current;
    if (!holding || !root) return;
    let stop: (() => void) | undefined;
    let cancelled = false;
    void loadDefer().then(
      ({ holdUntilGone }) => {
        if (cancelled) return;
        stop = holdUntilGone(root, {
          drop: () => {
            flushSync(() => {
              setMounted(false);
              setExpanded(false);
              setSettled(false);
            });
            latest.current.onExited?.();
          },
          close: () => setLetGo(true),
        });
      },
      // Without its chunk it could never leave: it closes as usual instead.
      () => {
        if (!cancelled) setLetGo(true);
      },
    );
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [holding]);

  // Following the content's height while open (`collapse-follow.ts`); the cleanup lands at once.
  const follower = useRef<HeightFollower | null>(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    const clip = clipRef.current;
    const measure = measureRef.current;
    if (!follow || !open || instant || !root || !clip || !measure || typeof ResizeObserver === "undefined" || !canMove()) return;
    let cancelled = false;
    // Without its chunk the height lands at once, as it does at rest.
    void loadFollow().then(
      ({ followHeight }) => {
        if (!cancelled) follower.current = followHeight(root, clip, measure);
      },
      () => {},
    );
    return () => {
      cancelled = true;
      follower.current?.stop();
      follower.current = null;
    };
  }, [follow, open, instant]);
  // What this render changed is pinned in its own commit: no frame (nor a
  // read in the next one's rAF) sees the rows at the new height before they travel.
  useLayoutEffect(() => {
    follower.current?.sync();
  });

  if (!open && !mounted) return null;
  const shown = open ? expanded : holding;
  return (
    <div
      ref={rootRef}
      id={id}
      data-testid={testId}
      data-state={shown ? "open" : open ? "entering" : "closed"}
      data-settled={(open || holding) && settled ? "" : undefined}
      data-instant={instant ? "" : undefined}
      className={cn("ui-collapse", className)}
    >
      <div ref={clipRef} className={cn("collapse-content", contentClassName)}>
        {/* The measure holds its children's margins (`flow-root`): a first child's margin-top that collapsed
            through it was missing from what the follower read, so the rows were pinned 8 px short and landed
            with a jump. The clip, a grid item, already held them: nothing moves at rest. */}
        {measured ? (
          <div ref={measureRef} className="flow-root">
            {open ? children : held}
          </div>
        ) : open ? (
          children
        ) : (
          held
        )}
      </div>
    </div>
  );
}
